<script setup>
import { ref, onMounted } from 'vue'
import { fetchUrlSources } from '../utils/common'

const emit = defineEmits(['next-step'])

const uploadStatus = ref('')
const imageUploadStatus = ref('')
const videoUploadStatus = ref('')
const taskStatus = ref('')
const taskResult = ref('')
const videoUrl = ref('')
const imageId = ref('')
const targetVideoId = ref('')
const imagePreview = ref('')
const videoPreview = ref('')
const imageUrl = ref('')
const imageSourcePrefix = ref('素材模板/图片')
const videoSourcePrefix = ref('素材模板/视频')
const imageSourceUrls = ref([])
const videoSourceUrls = ref([])
const loading = ref(false)
const resultVideoUrl = ref('')

onMounted(() => {
  emit('mounted')
})
const apiKey = import.meta.env.VITE_VEDIO_KEY
// 上传文件
async function handleUrlInput(type) {
  const url = type === 'image' ? imageUrl.value : videoUrl.value
  if (!url) return

  const statusRef = type === 'image' ? imageUploadStatus : videoUploadStatus
  
  try {
    // 更新预览
    if (type === 'image') {
      imagePreview.value = url
    } else {
      videoPreview.value = url
    }

    // 使用 URL 调用上传接口
    const jsonData = {
      src_url: url,
      file_type: type,
      file_alias: url.split('/').pop(),
      ext: url.split('.').pop(),
      attrs: { from: 'source' }
    }

    const response = await fetch('/paas/file/upload/link', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(jsonData)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    statusRef.value = `${new Date().toLocaleTimeString()} URL 处理成功，任务ID: ${data.data.task_id}`
    checkUploadStatus(data.data.task_id, statusRef)
  } catch (error) {
    console.error('URL processing error:', error)
    statusRef.value = `${new Date().toLocaleTimeString()} URL 处理失败: ${error.message || '服务器错误'}`
  }
}

// 运行Image2Video任务
async function runImage2VideoTask() {
    if (!imageId.value || !targetVideoId.value) {
        alert('请输入模板图ID和模板视频ID')
        return
    }

    // 清空上一次的结果
    taskStatus.value = ''
    taskResult.value = ''
    resultVideoUrl.value = ''

    const data = {
        image_id: imageId.value,
        target_video_id: targetVideoId.value,
        seed: 42,
        steps: 25,
        guidance_scale_list: [3.5, 7.1, 3.2],
        sample_rate: 2,
        total_frames: 78
    }

    try {
        loading.value = true
        const response = await fetch('/paas/task/async/i2v_infer?api-version=20240611', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }
        const result = await response.json()
        taskStatus.value = '任务提交成功，任务ID: ' + result.data.task_id
        checkTaskStatus(result.data.task_id)
    } catch (error) {
        console.error('Task error:', error)
        taskStatus.value = '任务提交失败: ' + (error.message || '服务器错误')
        loading.value = false
    }
}

// 检查任务状态
async function checkTaskStatus(taskId) {
    try {
        const response = await fetch(`/paas/task/async/${taskId}/status`, {
            headers: {
                'Authorization': `Bearer ${apiKey}`
            }
        })
        const data = await response.json()
        
        if (data.data.status === 'success') {
            taskStatus.value = '任务已完成，正在获取结果...'
            getTaskResult(taskId)
        } else if (data.data.status === 'failed') {
            taskStatus.value = '任务失败'
            loading.value = false
        } else {
            setTimeout(() => checkTaskStatus(taskId), 3000)
        }
    } catch (error) {
        taskStatus.value = '检查任务状态失败: ' + error.message
        loading.value = false
    }
}

// 获取任务结果
async function getTaskResult(taskId) {
    try {
        const response = await fetch(`/paas/task/async/${taskId}/result`, {
            headers: {
                'Authorization': `Bearer ${apiKey}`
            }
        })
        const data = await response.json()
        
        if (data.status === 'ok') {
            taskResult.value = `${new Date().toLocaleTimeString()} 任务结果：\n视频ID: ${data.video_id}\n视频URL: ${data.video_url}`
            resultVideoUrl.value = data.video_url
        } else {
            taskResult.value = '获取任务结果失败'
            resultVideoUrl.value = ''
        }
    } catch (error) {
        taskResult.value = '获取任务结果失败: ' + error.message
        resultVideoUrl.value = ''
    } finally {
        loading.value = false
    }
}

async function checkUploadStatus(taskId, statusRef) {
    try {
        const response = await fetch(`/paas/file/upload/${taskId}/status`, {
            headers: {
                'Authorization': `Bearer ${apiKey}`
            }
        })
        const data = await response.json()
        
        if (data.data.status === 'success') {
            statusRef.value += `\n${new Date().toLocaleTimeString()} 上传已完成，正在获取结果...`
            getUploadResult(taskId, statusRef)
        } else if (data.data.status === 'failed') {
            statusRef.value += `\n${new Date().toLocaleTimeString()} 上传失败`
        } else {
            setTimeout(() => checkUploadStatus(taskId, statusRef), 3000)
        }
    } catch (error) {
        statusRef.value += `\n${new Date().toLocaleTimeString()} 检查任务状态失败: ${error.message}`
    }
}

// 获取任务结果
async function getUploadResult(taskId, statusRef) {
    try {
        const response = await fetch(`/paas/file/upload/${taskId}/result`, {
            headers: {
                'Authorization': `Bearer ${apiKey}`
            }
        })
        const data = await response.json()
        
        if (data.status === 'ok') {
            statusRef.value += `\n${new Date().toLocaleTimeString()} 文件ID: ${data.data.file_id}`
            if (statusRef === imageUploadStatus) {
                imageId.value = data.data.file_id
            } else if (statusRef === videoUploadStatus) {
                targetVideoId.value = data.data.file_id
            }
        } else {
            statusRef.value = '文件上传失败'
        }
    } catch (error) {
        statusRef.value = '文件上传失败: ' + error.message
    }
}

// 轮询生成结果
async function pollGenerateResult(taskId) {
    const checkResult = async () => {
        try {
            const response = await fetch(`/mhapi/v1/imgenstd/task/${taskId}`, {
                headers: {
                    'Authorization': `Bearer ${token.value}`,
                    'Content-Type': 'application/json'
                }
            })
            const data = await response.json()
            
            if (data.task.state === 1) {
                resultImage.value = data.task.img_raw_url
                taskStatus.value = '生成完成'
                loading.value = false
                // 自动填充生成的文件ID
                imageId.value = data.task.id
                targetVideoId.value = data.task.id
            } else {
                setTimeout(checkResult, 1000)
            }
        } catch (error) {
            console.error('检查任务状态失败:', error)
            taskStatus.value = '检查任务状态失败'
            loading.value = false
        }
    }
    
    checkResult()
}

// 在视频生成成功后调用
async function handleVideoSuccess() {
  // ... 原有的成功处理逻辑 ...
  
  // 视频生成成功后，触发下一步
  emit('next-step')
}

// 添加新方法:
async function loadSourceImages(type) {
  try {
    loading.value = true
    const statusRef = type === 'image' ? imageUploadStatus : videoUploadStatus
    statusRef.value = '正在加载素材库...'
    
    const prefix = type === 'image' ? imageSourcePrefix.value : videoSourcePrefix.value
    const urls = await fetchUrlSources(prefix)
    if (type === 'image') {
      imageSourceUrls.value = urls
      statusRef.value = `已加载 ${imageSourceUrls.value.length} 张图片`
    } else {
      videoSourceUrls.value = urls
      statusRef.value = `已加载 ${videoSourceUrls.value.length} 个视频`
    }
  } catch (error) {
    console.error('加载素材库失败:', error)
    statusRef.value = '加载素材库失败: ' + error.message
  } finally {
    loading.value = false
  }
}

function selectSourceFile(url, type) {
  if (type === 'image') {
    imageUrl.value = url
    handleUrlInput('image')
  } else {
    videoUrl.value = url
    handleUrlInput('video')
  }
}
</script>

<template>
    <div class="video-gen-container">
        <div class="video-gen">
            <div class="section">
                <h2>素材上传</h2>
                <div class="upload-grid">
                    <div class="upload-box">
                        <h3>图片上传</h3>
                        <div class="upload-container">
                            <div class="url-input-group">
                                <input 
                                    type="text" 
                                    v-model="imageUrl" 
                                    placeholder="输入图片URL"
                                    @change="handleUrlInput('image')"
                                    class="url-input"
                                >
                            </div>
                            
                            <div class="source-section">
                                <div class="source-controls">
                                    <input 
                                        type="text" 
                                        v-model="imageSourcePrefix"
                                        placeholder="输入图片素材库前缀"
                                        class="prefix-input"
                                    >
                                    <button 
                                        @click="loadSourceImages('image')"
                                        :disabled="loading"
                                        class="source-button"
                                    >
                                        加载素材
                                    </button>
                                </div>
                                
                                <div v-if="imageSourceUrls.length" class="source-grid">
                                    <div 
                                        v-for="(url, index) in imageSourceUrls" 
                                        :key="index" 
                                        class="source-item"
                                        @click="selectSourceFile(url, 'image')"
                                    >
                                        <img :src="url" :alt="`素材 ${index + 1}`">
                                    </div>
                                </div>
                            </div>

                            <div class="preview" v-if="imagePreview">
                                <img :src="imagePreview" class="preview-image" />
                            </div>
                        </div>
                        <pre class="status-log" v-if="imageUploadStatus">{{ imageUploadStatus }}</pre>
                    </div>
                    <div class="upload-box">
                        <h3>视频上传</h3>
                        <div class="upload-container">
                            <div class="url-input-group">
                                <input 
                                    type="text" 
                                    v-model="videoUrl" 
                                    placeholder="输入视频URL"
                                    @change="handleUrlInput('video')"
                                    class="url-input"
                                >
                            </div>
                            
                            <div class="source-section">
                                <div class="source-controls">
                                    <input 
                                        type="text" 
                                        v-model="videoSourcePrefix"
                                        placeholder="输入视频素材库前缀"
                                        class="prefix-input"
                                    >
                                    <button 
                                        @click="loadSourceImages('video')"
                                        :disabled="loading"
                                        class="source-button"
                                    >
                                        加载素材
                                    </button>
                                </div>
                                
                                <div v-if="videoSourceUrls.length" class="source-grid">
                                    <div 
                                        v-for="(url, index) in videoSourceUrls" 
                                        :key="index" 
                                        class="source-item"
                                        @click="selectSourceFile(url, 'video')"
                                    >
                                        <video 
                                            :src="url" 
                                            class="preview-video"
                                            preload="metadata"
                                        ></video>
                                    </div>
                                </div>
                            </div>

                            <div class="preview" v-if="videoPreview">
                                <video :src="videoPreview" controls class="preview-video"></video>
                            </div>
                        </div>
                        <pre class="status-log" v-if="videoUploadStatus">{{ videoUploadStatus }}</pre>
                    </div>
                </div>
            </div>

            <div class="section">
                <h2>Image2Video任务</h2>
                <div class="task-container">
                    <div class="input-group">
                        <label for="imageId">模板图ID:</label>
                        <input 
                            type="text" 
                            id="imageId" 
                            v-model="imageId" 
                            placeholder="将自动填充已上传图片的ID"
                            readonly
                        >
                    </div>
                    <div class="input-group">
                        <label for="targetVideoId">模板视频ID:</label>
                        <input 
                            type="text" 
                            id="targetVideoId" 
                            v-model="targetVideoId" 
                            placeholder="将自动填充已上传视频ID"
                            readonly
                        >
                    </div>
                    <button @click="runImage2VideoTask" :disabled="!imageId || !targetVideoId">
                        {{ loading ? '处理中...' : '开始生成' }}
                    </button>
                </div>
                <p class="status">{{ taskStatus }}</p>
                <pre class="status-log" v-if="taskResult">{{ taskResult }}</pre>
                <div v-if="resultVideoUrl" class="result-video-player">
                    <h3>生成结果</h3>
                    <div class="video-container">
                        <video controls>
                            <source :src="resultVideoUrl" type="video/mp4">
                            您的浏览器不支持视频播放
                        </video>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.video-gen-container {
    width: 100%;
    padding: 20px;
    background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
    box-sizing: border-box;
    border-radius: 16px;
}

.video-gen {
    margin: 0 auto;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: 0 15px 50px rgba(0,0,0,0.2);
    backdrop-filter: blur(5px);
    animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.section {
    margin-bottom: 30px;
    padding: 25px;
    background: var(--section-bg);
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    transition: transform 0.3s ease;
}

.section:hover {
    transform: translateY(-2px);
}

h2 {
    color: var(--text-color);
    margin-bottom: 20px;
    font-size: 1.8em;
    font-weight: 600;
    background: linear-gradient(45deg, var(--gradient-start), var(--gradient-end));
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.status-log {
    margin: 15px 0;
    padding: 15px;
    background: var(--log-bg);
    color: var(--log-text);
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    white-space: pre-wrap;
    max-height: 150px;
    overflow-y: auto;
    font-size: 0.9em;
    line-height: 1.5;
    border: 1px solid var(--border-color);
}

.upload-container,
.task-container {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.input-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

label {
    color: var(--text-color);
    font-size: 0.9em;
}

input[type="text"],
input[type="file"] {
    padding: 12px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    font-size: 16px;
    transition: all 0.3s ease;
    background: var(--input-bg);
    color: var(--text-color);
}

input[type="text"]:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-shadow);
}

button {
    padding: 12px 25px;
    background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.3s ease;
    align-self: flex-start;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 500;
}

button:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px var(--focus-shadow);
}

.status {
    margin-top: 10px;
    color: var(--subtitle-color);
}

.result {
    margin-top: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #e1e1e1;
}

.upload-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
    margin: 30px 0;
}

.upload-box {
    background: var(--card-bg);
    border-radius: 16px;
    padding: 25px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.upload-container {
    display: flex;
    flex-direction: column;
    gap: 25px;
    padding: 20px;
    border: 2px dashed var(--border-color);
    border-radius: 12px;
    background: var(--section-bg);
}

.source-section {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.source-controls {
    display: flex;
    gap: 12px;
    align-items: center;
}

.source-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 15px;
    max-height: 300px;
    overflow-y: auto;
    padding: 15px;
    background: var(--section-bg);
    border-radius: 10px;
}

.source-item {
    aspect-ratio: 16/9;
    border: 2px solid transparent;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    background: var(--card-bg);
}

.source-item:hover {
    border-color: var(--primary-color);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.preview {
    margin-top: 20px;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.status-log {
    margin-top: 20px;
    padding: 15px;
    border-radius: 8px;
    font-size: 0.9em;
    line-height: 1.6;
}

.video-player {
    margin-top: 20px;
    width: 90%;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px var(--card-shadow);
}

.video-player video {
    width: 100%;
    max-height: 600px;
    display: block;
    background: var(--log-bg);
    object-fit: contain;
}

.preview-image {
    max-width: 100%;
    max-height: 300px;
    border-radius: 4px;
    object-fit: contain;
}

.preview-video {
    max-width: 100%;
    max-height: 300px;
    border-radius: 4px;
    background: #000;
}

@media (max-width: 1200px) {
    .upload-grid {
        grid-template-columns: 1fr;
    }

    .preview-image,
    .preview-video {
        max-height: 250px;
    }
}

.url-input-group {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
}

.url-input-group input {
    flex: 1;
    padding: 12px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    font-size: 16px;
    background: var(--input-bg);
    color: var(--text-color);
}

.upload-button {
    padding: 12px 20px;
    background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;
    text-align: center;
}

.upload-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--focus-shadow);
}

.preview {
    margin-top: 15px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    padding: 10px;
    background: var(--input-bg);
}

.url-input {
    width: 100%;
    padding: 12px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    font-size: 16px;
    background: var(--input-bg);
    color: var(--text-color);
}

.url-input:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-shadow);
}

.preview {
    margin-top: 15px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    padding: 10px;
    background: var(--input-bg);
    text-align: center;
}

.preview-image {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    object-fit: contain;
}

.preview-video {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    background: #000;
}

.source-controls {
    display: flex;
    gap: 10px;
    margin-top: 10px;
}

.prefix-input {
    flex: 1;
    padding: 8px 12px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    font-size: 14px;
    background: var(--input-bg);
    color: var(--text-color);
}

.source-button {
    white-space: nowrap;
    padding: 8px 16px;
}

.source-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
    margin-top: 15px;
    max-height: 300px;
    overflow-y: auto;
    padding: 10px;
    background: var(--section-bg);
    border-radius: 8px;
}

.source-item {
    aspect-ratio: 1;
    border: 2px solid transparent;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
}

.source-item:hover {
    border-color: var(--primary-color);
    transform: scale(1.05);
}

.source-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.result-video-player {
    margin-top: 30px;
    padding: 25px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.result-video-player h3 {
    margin-bottom: 20px;
    color: var(--text-color);
    font-size: 1.2em;
}

.video-container {
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 宽高比 */
    background: var(--section-bg);
    border-radius: 8px;
    overflow: hidden;
}

.video-container video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
}

.input-group input {
    padding: 12px;
    border: 2px solid var(--border-color);
    border-radius: 8px;
    font-size: 16px;
    background: var(--input-bg);
    color: var(--text-color);
    cursor: not-allowed;
}

.input-group input:read-only {
    background: var(--section-bg);
    opacity: 0.8;
}
</style> 